import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, CircularProgress, Tab, Tabs } from '@mui/material';

import { useAccessControlApi } from '../model/useAccessControlApi';
import { useSsoMappingsApi } from '../model/useSsoMappingsApi';
import { GroupsPanel } from './GroupsPanel';
import { SsoMappingsPanel } from './SsoMappingsPanel';
import { UsersPanel } from './UsersPanel';
import { useStyles } from './styles';

export interface ExtraTab {
  key: string;
  label: string;
  /** Shown as a badge next to the label once known. */
  count?: number;
  content: React.ReactNode;
}

interface AccessControlTabsProps {
  /** Set on the platform-admin screens; the signed-in user's own company otherwise. */
  managedCompanyId?: string;
  /** More tabs of the hosting page, shown after Users and Groups in the same row. */
  extraTabs?: ExtraTab[];
}

export const AccessControlTabs: React.FC<AccessControlTabsProps> = ({
  managedCompanyId,
  extraTabs = [],
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  const api = useAccessControlApi(managedCompanyId);
  // Mappings are written by the platform admin only, and only where SSO is switched on.
  const sso = useSsoMappingsApi(managedCompanyId);
  const showSso = managedCompanyId !== undefined && !sso.isSsoDisabled;
  const tabs = [
    ...(api.canManageUsers ? ['users'] : []),
    'groups',
    ...(showSso ? ['sso'] : []),
    ...extraTabs.map((tab) => tab.key),
  ];
  const [active, setActive] = useState(0);
  const [groupFilter, setGroupFilter] = useState('');
  const current = tabs[Math.min(active, tabs.length - 1)];
  const extra = extraTabs.find((tab) => tab.key === current);

  const counts: Record<string, number | undefined> = {
    users: api.isUsersLoading ? undefined : api.users.length,
    groups: api.isGroupsLoading ? undefined : api.groups.length,
    sso: sso.isLoading ? undefined : sso.mappings.length,
    ...Object.fromEntries(extraTabs.map((tab) => [tab.key, tab.count])),
  };

  const showMembers = (groupId: string) => {
    setGroupFilter(groupId);
    setActive(tabs.indexOf('users'));
  };

  if (api.isActorLoading) {
    return (
      <Box className={classes.loader}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Tabs
        value={tabs.indexOf(current)}
        onChange={(_, index: number) => setActive(index)}
        className={classes.tabs}>
        {tabs.map((tab) => (
          <Tab
            key={tab}
            data-testid={`access-tab-${tab}`}
            label={
              <Box className={classes.tabLabel}>
                {extraTabs.find((extraTab) => extraTab.key === tab)?.label ??
                  t(`accessControl.tabs.${tab}`)}
                {counts[tab] !== undefined && (
                  <Box component="span" className={classes.tabCount}>
                    {counts[tab]}
                  </Box>
                )}
              </Box>
            }
          />
        ))}
      </Tabs>
      {current === 'users' && (
        <UsersPanel api={api} groupFilter={groupFilter} onGroupFilterChange={setGroupFilter} />
      )}
      {current === 'groups' && <GroupsPanel api={api} onShowMembers={showMembers} />}
      {current === 'sso' && <SsoMappingsPanel api={api} sso={sso} />}
      {extra && <Box className={classes.extraTab}>{extra.content}</Box>}
    </Box>
  );
};
